import React, { useEffect } from 'react';

const KubernetesGuide = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Kubernetes Complete Guide - Container Orchestration 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Complete Kubernetes guide - Pods, Services, Deployments, ConfigMaps, Secrets. Learn container orchestration with practical examples and best practices.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Complete Kubernetes guide - Pods, Services, Deployments, ConfigMaps, Secrets. Learn container orchestration with practical examples and best practices.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Kubernetes, container orchestration, pods, services, deployments, kubectl, K8s, microservices, cloud native');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'Kubernetes, container orchestration, pods, services, deployments, kubectl, K8s, microservices, cloud native';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Kubernetes Complete Guide</h1>
        <p style={styles.subtitle}>
          Container Orchestration Platform for Modern Applications
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Kubernetes?</h2>
          <p style={styles.paragraph}>
            Kubernetes (K8s) is an open-source container orchestration platform that automates the 
            deployment, scaling, and management of containerized applications. Originally developed 
            by Google, it provides a robust framework for running distributed systems at scale with 
            features like service discovery, load balancing, storage orchestration, and automated rollouts.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Kubernetes Architecture</h2>
          
          <div style={styles.codeBlock}>
{`# Kubernetes Cluster Architecture

┌─────────────────────────────────────────────────────────────┐
│                    Master Nodes                             │
├─────────────────────────────────────────────────────────────┤
│  API Server  │  etcd  │  Scheduler  │  Controller Manager   │
├─────────────────────────────────────────────────────────────┤
│                    Worker Nodes                             │
├─────────────────────────────────────────────────────────────┤
│ Node 1          │ Node 2          │ Node 3                  │
│ ┌─────────────┐ │ ┌─────────────┐ │ ┌─────────────┐         │
│ │   kubelet   │ │ │   kubelet   │ │ │   kubelet   │         │
│ │ kube-proxy  │ │ │ kube-proxy  │ │ │ kube-proxy  │         │
│ │ Container   │ │ │ Container   │ │ │ Container   │         │
│ │ Runtime     │ │ │ Runtime     │ │ │ Runtime     │         │
│ └─────────────┘ │ └─────────────┘ │ └─────────────┘         │
│ ┌─────────────┐ │ ┌─────────────┐ │ ┌─────────────┐         │
│ │    Pods     │ │ │    Pods     │ │ │    Pods     │         │
│ └─────────────┘ │ └─────────────┘ │ └─────────────┘         │
└─────────────────────────────────────────────────────────────┘

# Control Plane Components:
# - API Server: REST API for all cluster operations
# - etcd: Distributed key-value store for cluster state
# - Scheduler: Assigns pods to nodes based on resources
# - Controller Manager: Runs controllers that manage cluster state

# Node Components:
# - kubelet: Agent that manages pods on the node
# - kube-proxy: Network proxy for service communication
# - Container Runtime: Docker, containerd, or CRI-O`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Core Kubernetes Objects</h2>
          
          <h3 style={styles.heading}>1. Pods</h3>
          <p style={styles.paragraph}>
            Pods are the smallest deployable units in Kubernetes. A pod represents a single instance 
            of a running process and can contain one or more containers that share storage and network.
          </p>

          <div style={styles.codeBlock}>
{`# Simple Pod Definition
apiVersion: v1
kind: Pod
metadata:
  name: web-app-pod
  labels:
    app: web-app
    version: v1.0
spec:
  containers:
  - name: web-app
    image: nginx:1.21-alpine
    ports:
    - containerPort: 80
      name: http
    resources:
      requests:
        memory: "128Mi"
        cpu: "100m"
      limits:
        memory: "256Mi"
        cpu: "200m"
    env:
    - name: ENV
      value: "production"
    volumeMounts:
    - name: config-volume
      mountPath: /etc/nginx/conf.d
  volumes:
  - name: config-volume
    configMap:
      name: nginx-config

---
# Multi-container Pod (Sidecar Pattern)
apiVersion: v1
kind: Pod
metadata:
  name: app-with-sidecar
  labels:
    app: web-app
spec:
  containers:
  # Main application container
  - name: web-app
    image: my-web-app:latest
    ports:
    - containerPort: 8080
    volumeMounts:
    - name: shared-logs
      mountPath: /var/log/app
  
  # Sidecar container for log shipping
  - name: log-shipper
    image: fluent/fluent-bit:latest
    volumeMounts:
    - name: shared-logs
      mountPath: /var/log/app
    - name: fluent-bit-config
      mountPath: /fluent-bit/etc
  
  volumes:
  - name: shared-logs
    emptyDir: {}
  - name: fluent-bit-config
    configMap:
      name: fluent-bit-config

# Pod with Init Containers
apiVersion: v1
kind: Pod
metadata:
  name: app-with-init
spec:
  initContainers:
  # Run database migrations before starting main app
  - name: db-migration
    image: my-app:latest
    command: ['npm', 'run', 'migrate']
    env:
    - name: DATABASE_URL
      valueFrom:
        secretKeyRef:
          name: db-secret
          key: url
  
  # Wait for database to be ready
  - name: wait-for-db
    image: busybox:1.35
    command: ['sh', '-c', 'until nc -z postgres-service 5432; do sleep 1; done']
  
  containers:
  - name: web-app
    image: my-app:latest
    ports:
    - containerPort: 3000

# kubectl commands for pods
kubectl get pods                           # List all pods
kubectl get pods -o wide                   # Show more details
kubectl describe pod web-app-pod           # Detailed pod information
kubectl logs web-app-pod                   # View pod logs
kubectl logs web-app-pod -c log-shipper    # Logs from specific container
kubectl exec -it web-app-pod -- /bin/bash  # Execute shell in pod
kubectl delete pod web-app-pod             # Delete pod
kubectl port-forward web-app-pod 8080:80   # Forward local port to pod`}
          </div>

          <h3 style={styles.heading}>2. Deployments</h3>
          <p style={styles.paragraph}>
            Deployments provide declarative updates for pods and ReplicaSets. They manage the lifecycle 
            of applications, including rolling updates, rollbacks, and scaling.
          </p>

          <div style={styles.codeBlock}>
{`# Deployment Definition
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-app-deployment
  labels:
    app: web-app
spec:
  replicas: 3
  strategy:
    type: RollingUpdate
    rollingUpdate:
      maxSurge: 1
      maxUnavailable: 1
  selector:
    matchLabels:
      app: web-app
  template:
    metadata:
      labels:
        app: web-app
        version: v1.2.0
    spec:
      containers:
      - name: web-app
        image: my-web-app:v1.2.0
        ports:
        - containerPort: 8080
          name: http
        resources:
          requests:
            memory: "256Mi"
            cpu: "250m"
          limits:
            memory: "512Mi"
            cpu: "500m"
        livenessProbe:
          httpGet:
            path: /health
            port: 8080
          initialDelaySeconds: 30
          periodSeconds: 10
        readinessProbe:
          httpGet:
            path: /ready
            port: 8080
          initialDelaySeconds: 5
          periodSeconds: 5
        env:
        - name: DATABASE_URL
          valueFrom:
            secretKeyRef:
              name: db-credentials
              key: url
        - name: REDIS_URL
          valueFrom:
            configMapKeyRef:
              name: app-config
              key: redis-url

---
# Blue-Green Deployment Strategy
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-app-blue
  labels:
    app: web-app
    version: blue
spec:
  replicas: 3
  selector:
    matchLabels:
      app: web-app
      version: blue
  template:
    metadata:
      labels:
        app: web-app
        version: blue
    spec:
      containers:
      - name: web-app
        image: my-web-app:v1.1.0
        ports:
        - containerPort: 8080

---
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-app-green
  labels:
    app: web-app
    version: green
spec:
  replicas: 0  # Initially scaled to 0
  selector:
    matchLabels:
      app: web-app
      version: green
  template:
    metadata:
      labels:
        app: web-app
        version: green
    spec:
      containers:
      - name: web-app
        image: my-web-app:v1.2.0
        ports:
        - containerPort: 8080

# Deployment management commands
kubectl apply -f deployment.yaml                    # Create/update deployment
kubectl get deployments                            # List deployments
kubectl describe deployment web-app-deployment     # Deployment details
kubectl scale deployment web-app-deployment --replicas=5  # Scale deployment
kubectl set image deployment/web-app-deployment web-app=my-web-app:v1.3.0  # Update image
kubectl rollout status deployment/web-app-deployment  # Check rollout status
kubectl rollout history deployment/web-app-deployment # View rollout history
kubectl rollout undo deployment/web-app-deployment    # Rollback to previous version
kubectl rollout undo deployment/web-app-deployment --to-revision=2  # Rollback to specific revision`}
          </div>

          <h3 style={styles.heading}>3. Services</h3>
          <p style={styles.paragraph}>
            Services provide stable network endpoints for accessing pods. They enable service discovery 
            and load balancing across multiple pod replicas.
          </p>

          <div style={styles.codeBlock}>
{`# ClusterIP Service (Internal)
apiVersion: v1
kind: Service
metadata:
  name: web-app-service
  labels:
    app: web-app
spec:
  type: ClusterIP
  selector:
    app: web-app
  ports:
  - name: http
    port: 80
    targetPort: 8080
    protocol: TCP

---
# NodePort Service (External Access)
apiVersion: v1
kind: Service
metadata:
  name: web-app-nodeport
spec:
  type: NodePort
  selector:
    app: web-app
  ports:
  - name: http
    port: 80
    targetPort: 8080
    nodePort: 30080  # Accessible on all nodes at port 30080

---
# LoadBalancer Service (Cloud Provider)
apiVersion: v1
kind: Service
metadata:
  name: web-app-loadbalancer
  annotations:
    service.beta.kubernetes.io/aws-load-balancer-type: "nlb"
    service.beta.kubernetes.io/aws-load-balancer-backend-protocol: "http"
spec:
  type: LoadBalancer
  selector:
    app: web-app
  ports:
  - name: http
    port: 80
    targetPort: 8080
  - name: https
    port: 443
    targetPort: 8080

---
# Headless Service (Direct Pod Access)
apiVersion: v1
kind: Service
metadata:
  name: database-headless
spec:
  clusterIP: None  # Headless service
  selector:
    app: database
  ports:
  - name: postgres
    port: 5432
    targetPort: 5432

---
# ExternalName Service (External Service Mapping)
apiVersion: v1
kind: Service
metadata:
  name: external-api
spec:
  type: ExternalName
  externalName: api.external-service.com
  ports:
  - name: https
    port: 443

# Service discovery examples
# Pods can access services by name:
# http://web-app-service
# http://web-app-service.default.svc.cluster.local
# http://web-app-service:80

# Service management commands
kubectl get services                        # List services
kubectl describe service web-app-service    # Service details
kubectl get endpoints web-app-service       # Show service endpoints
kubectl port-forward service/web-app-service 8080:80  # Port forward to service`}
          </div>

          <h3 style={styles.heading}>4. ConfigMaps and Secrets</h3>
          <p style={styles.paragraph}>
            ConfigMaps store non-confidential configuration data, while Secrets store sensitive 
            information like passwords, tokens, and keys.
          </p>

          <div style={styles.codeBlock}>
{`# ConfigMap Definition
apiVersion: v1
kind: ConfigMap
metadata:
  name: app-config
data:
  # Simple key-value pairs
  database.host: "postgres-service"
  database.port: "5432"
  redis.url: "redis://redis-service:6379"
  log.level: "info"
  
  # Configuration file
  nginx.conf: |
    server {
        listen 80;
        server_name localhost;
        
        location / {
            proxy_pass http://web-app-service:8080;
            proxy_set_header Host $host;
            proxy_set_header X-Real-IP $remote_addr;
        }
        
        location /health {
            access_log off;
            return 200 "healthy\n";
        }
    }
  
  # JSON configuration
  app.json: |
    {
      "server": {
        "port": 8080,
        "host": "0.0.0.0"
      },
      "database": {
        "pool": {
          "min": 2,
          "max": 10
        }
      },
      "features": {
        "enableMetrics": true,
        "enableTracing": false
      }
    }

---
# Secret Definition
apiVersion: v1
kind: Secret
metadata:
  name: db-credentials
type: Opaque
data:
  # Base64 encoded values
  username: cG9zdGdyZXM=  # postgres
  password: c2VjcmV0MTIz    # secret123
  url: cG9zdGdyZXNxbDovL3Bvc3RncmVzOnNlY3JldDEyM0Bwb3N0Z3Jlcy1zZXJ2aWNlOjU0MzIvbXlhcHA=

---
# TLS Secret
apiVersion: v1
kind: Secret
metadata:
  name: tls-secret
type: kubernetes.io/tls
data:
  tls.crt: LS0tLS1CRUdJTi... # Base64 encoded certificate
  tls.key: LS0tLS1CRUdJTi... # Base64 encoded private key

---
# Docker Registry Secret
apiVersion: v1
kind: Secret
metadata:
  name: docker-registry-secret
type: kubernetes.io/dockerconfigjson
data:
  .dockerconfigjson: eyJhdXRocyI6eyJyZWdpc3RyeS5leGFtcGxlLmNvbSI6eyJ1c2VybmFtZSI6InVzZXIiLCJwYXNzd29yZCI6InBhc3MifX19

# Using ConfigMaps and Secrets in Pods
apiVersion: v1
kind: Pod
metadata:
  name: app-with-config
spec:
  containers:
  - name: app
    image: my-app:latest
    
    # Environment variables from ConfigMap
    env:
    - name: DATABASE_HOST
      valueFrom:
        configMapKeyRef:
          name: app-config
          key: database.host
    - name: LOG_LEVEL
      valueFrom:
        configMapKeyRef:
          name: app-config
          key: log.level
    
    # Environment variables from Secret
    - name: DATABASE_PASSWORD
      valueFrom:
        secretKeyRef:
          name: db-credentials
          key: password
    
    # All ConfigMap keys as environment variables
    envFrom:
    - configMapRef:
        name: app-config
    - secretRef:
        name: db-credentials
    
    # Mount ConfigMap as volume
    volumeMounts:
    - name: config-volume
      mountPath: /etc/config
    - name: nginx-config
      mountPath: /etc/nginx/conf.d
    - name: secret-volume
      mountPath: /etc/secrets
      readOnly: true
  
  volumes:
  - name: config-volume
    configMap:
      name: app-config
  - name: nginx-config
    configMap:
      name: app-config
      items:
      - key: nginx.conf
        path: default.conf
  - name: secret-volume
    secret:
      secretName: db-credentials
      defaultMode: 0400  # Read-only for owner

  # Use Docker registry secret
  imagePullSecrets:
  - name: docker-registry-secret

# ConfigMap and Secret management
kubectl create configmap app-config --from-file=config/  # From directory
kubectl create configmap app-config --from-file=app.properties  # From file
kubectl create configmap app-config --from-literal=key1=value1 --from-literal=key2=value2

kubectl create secret generic db-credentials --from-literal=username=postgres --from-literal=password=secret123
kubectl create secret tls tls-secret --cert=tls.crt --key=tls.key
kubectl create secret docker-registry docker-secret --docker-server=registry.com --docker-username=user --docker-password=pass

kubectl get configmaps
kubectl get secrets
kubectl describe configmap app-config
kubectl describe secret db-credentials`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Advanced Kubernetes Concepts</h2>
          
          <h3 style={styles.heading}>1. Ingress</h3>
          <p style={styles.paragraph}>
            Ingress manages external access to services, typically HTTP/HTTPS. It provides load 
            balancing, SSL termination, and name-based virtual hosting.
          </p>

          <div style={styles.codeBlock}>
{`# Ingress Controller (NGINX)
apiVersion: apps/v1
kind: Deployment
metadata:
  name: nginx-ingress-controller
  namespace: ingress-nginx
spec:
  replicas: 2
  selector:
    matchLabels:
      app: nginx-ingress-controller
  template:
    metadata:
      labels:
        app: nginx-ingress-controller
    spec:
      containers:
      - name: nginx-ingress-controller
        image: k8s.gcr.io/ingress-nginx/controller:v1.8.1
        args:
        - /nginx-ingress-controller
        - --configmap=$(POD_NAMESPACE)/nginx-configuration
        - --tcp-services-configmap=$(POD_NAMESPACE)/tcp-services
        - --udp-services-configmap=$(POD_NAMESPACE)/udp-services
        env:
        - name: POD_NAME
          valueFrom:
            fieldRef:
              fieldPath: metadata.name
        - name: POD_NAMESPACE
          valueFrom:
            fieldRef:
              fieldPath: metadata.namespace
        ports:
        - name: http
          containerPort: 80
        - name: https
          containerPort: 443

---
# Simple Ingress
apiVersion: networking.k8s.io/v1
kind: Ingress
metadata:
  name: web-app-ingress
  annotations:
    nginx.ingress.kubernetes.io/rewrite-target: /
spec:
  rules:
  - host: myapp.example.com
    http:
      paths:
      - path: /
        pathType: Prefix
        backend:
          service:
            name: web-app-service
            port:
              number: 80

---
# Advanced Ingress with TLS and multiple paths
apiVersion: networking.k8s.io/v1
kind: Ingress
metadata:
  name: multi-service-ingress
  annotations:
    nginx.ingress.kubernetes.io/ssl-redirect: "true"
    nginx.ingress.kubernetes.io/use-regex: "true"
    nginx.ingress.kubernetes.io/rate-limit: "100"
    cert-manager.io/cluster-issuer: "letsencrypt-prod"
spec:
  tls:
  - hosts:
    - api.example.com
    - app.example.com
    secretName: example-com-tls
  
  rules:
  # API service
  - host: api.example.com
    http:
      paths:
      - path: /v1/users
        pathType: Prefix
        backend:
          service:
            name: user-service
            port:
              number: 8080
      - path: /v1/orders
        pathType: Prefix
        backend:
          service:
            name: order-service
            port:
              number: 8080
  
  # Web application
  - host: app.example.com
    http:
      paths:
      - path: /
        pathType: Prefix
        backend:
          service:
            name: frontend-service
            port:
              number: 80
      - path: /api
        pathType: Prefix
        backend:
          service:
            name: backend-service
            port:
              number: 8080

# Ingress management
kubectl get ingress
kubectl describe ingress web-app-ingress
kubectl get ingress -o yaml`}
          </div>

          <h3 style={styles.heading}>2. Persistent Volumes</h3>
          <p style={styles.paragraph}>
            Persistent Volumes (PV) provide storage that persists beyond the lifecycle of individual pods. 
            Persistent Volume Claims (PVC) request storage resources.
          </p>

          <div style={styles.codeBlock}>
{`# Persistent Volume
apiVersion: v1
kind: PersistentVolume
metadata:
  name: postgres-pv
spec:
  capacity:
    storage: 10Gi
  accessModes:
  - ReadWriteOnce
  persistentVolumeReclaimPolicy: Retain
  storageClassName: fast-ssd
  hostPath:
    path: /data/postgres  # For local development only

---
# AWS EBS Persistent Volume
apiVersion: v1
kind: PersistentVolume
metadata:
  name: aws-ebs-pv
spec:
  capacity:
    storage: 20Gi
  accessModes:
  - ReadWriteOnce
  persistentVolumeReclaimPolicy: Delete
  storageClassName: gp2
  awsElasticBlockStore:
    volumeID: vol-0123456789abcdef0
    fsType: ext4

---
# Persistent Volume Claim
apiVersion: v1
kind: PersistentVolumeClaim
metadata:
  name: postgres-pvc
spec:
  accessModes:
  - ReadWriteOnce
  resources:
    requests:
      storage: 10Gi
  storageClassName: fast-ssd

---
# Storage Class (Dynamic Provisioning)
apiVersion: storage.k8s.io/v1
kind: StorageClass
metadata:
  name: fast-ssd
provisioner: kubernetes.io/aws-ebs
parameters:
  type: gp3
  iops: "3000"
  throughput: "125"
  encrypted: "true"
allowVolumeExpansion: true
reclaimPolicy: Delete
volumeBindingMode: WaitForFirstConsumer

---
# StatefulSet with Persistent Storage
apiVersion: apps/v1
kind: StatefulSet
metadata:
  name: postgres
spec:
  serviceName: postgres-headless
  replicas: 3
  selector:
    matchLabels:
      app: postgres
  template:
    metadata:
      labels:
        app: postgres
    spec:
      containers:
      - name: postgres
        image: postgres:15
        env:
        - name: POSTGRES_DB
          value: myapp
        - name: POSTGRES_USER
          valueFrom:
            secretKeyRef:
              name: postgres-secret
              key: username
        - name: POSTGRES_PASSWORD
          valueFrom:
            secretKeyRef:
              name: postgres-secret
              key: password
        - name: PGDATA
          value: /var/lib/postgresql/data/pgdata
        ports:
        - containerPort: 5432
          name: postgres
        volumeMounts:
        - name: postgres-storage
          mountPath: /var/lib/postgresql/data
        livenessProbe:
          exec:
            command:
            - pg_isready
            - -U
            - $(POSTGRES_USER)
          initialDelaySeconds: 30
          periodSeconds: 10
        readinessProbe:
          exec:
            command:
            - pg_isready
            - -U
            - $(POSTGRES_USER)
          initialDelaySeconds: 5
          periodSeconds: 5
  
  volumeClaimTemplates:
  - metadata:
      name: postgres-storage
    spec:
      accessModes:
      - ReadWriteOnce
      resources:
        requests:
          storage: 20Gi
      storageClassName: fast-ssd

# Volume management commands
kubectl get pv                           # List persistent volumes
kubectl get pvc                          # List persistent volume claims
kubectl describe pv postgres-pv          # PV details
kubectl describe pvc postgres-pvc        # PVC details
kubectl get storageclass                 # List storage classes`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Kubernetes Monitoring and Logging</h2>
          
          <div style={styles.codeBlock}>
{`# Prometheus and Grafana Setup
# Prometheus ConfigMap
apiVersion: v1
kind: ConfigMap
metadata:
  name: prometheus-config
data:
  prometheus.yml: |
    global:
      scrape_interval: 15s
    
    scrape_configs:
    - job_name: 'kubernetes-pods'
      kubernetes_sd_configs:
      - role: pod
      relabel_configs:
      - source_labels: [__meta_kubernetes_pod_annotation_prometheus_io_scrape]
        action: keep
        regex: true
      - source_labels: [__meta_kubernetes_pod_annotation_prometheus_io_path]
        action: replace
        target_label: __metrics_path__
        regex: (.+)

---
# Prometheus Deployment
apiVersion: apps/v1
kind: Deployment
metadata:
  name: prometheus
spec:
  replicas: 1
  selector:
    matchLabels:
      app: prometheus
  template:
    metadata:
      labels:
        app: prometheus
    spec:
      containers:
      - name: prometheus
        image: prom/prometheus:latest
        ports:
        - containerPort: 9090
        volumeMounts:
        - name: config-volume
          mountPath: /etc/prometheus
        - name: storage-volume
          mountPath: /prometheus
        args:
        - '--config.file=/etc/prometheus/prometheus.yml'
        - '--storage.tsdb.path=/prometheus'
        - '--web.console.libraries=/etc/prometheus/console_libraries'
        - '--web.console.templates=/etc/prometheus/consoles'
        - '--storage.tsdb.retention.time=200h'
        - '--web.enable-lifecycle'
      volumes:
      - name: config-volume
        configMap:
          name: prometheus-config
      - name: storage-volume
        persistentVolumeClaim:
          claimName: prometheus-pvc

---
# Application with Prometheus Metrics
apiVersion: apps/v1
kind: Deployment
metadata:
  name: web-app-with-metrics
spec:
  replicas: 3
  selector:
    matchLabels:
      app: web-app
  template:
    metadata:
      labels:
        app: web-app
      annotations:
        prometheus.io/scrape: "true"
        prometheus.io/port: "8080"
        prometheus.io/path: "/metrics"
    spec:
      containers:
      - name: web-app
        image: my-web-app:latest
        ports:
        - containerPort: 8080
          name: http
        - containerPort: 9090
          name: metrics
        env:
        - name: METRICS_ENABLED
          value: "true"

---
# Fluentd for Log Collection
apiVersion: apps/v1
kind: DaemonSet
metadata:
  name: fluentd
  namespace: kube-system
spec:
  selector:
    matchLabels:
      name: fluentd
  template:
    metadata:
      labels:
        name: fluentd
    spec:
      tolerations:
      - key: node-role.kubernetes.io/master
        effect: NoSchedule
      containers:
      - name: fluentd
        image: fluent/fluentd-kubernetes-daemonset:v1-debian-elasticsearch
        env:
        - name: FLUENT_ELASTICSEARCH_HOST
          value: "elasticsearch.logging.svc.cluster.local"
        - name: FLUENT_ELASTICSEARCH_PORT
          value: "9200"
        resources:
          limits:
            memory: 200Mi
          requests:
            cpu: 100m
            memory: 200Mi
        volumeMounts:
        - name: varlog
          mountPath: /var/log
        - name: varlibdockercontainers
          mountPath: /var/lib/docker/containers
          readOnly: true
      volumes:
      - name: varlog
        hostPath:
          path: /var/log
      - name: varlibdockercontainers
        hostPath:
          path: /var/lib/docker/containers

# Health Checks and Probes
apiVersion: apps/v1
kind: Deployment
metadata:
  name: app-with-health-checks
spec:
  replicas: 3
  selector:
    matchLabels:
      app: web-app
  template:
    metadata:
      labels:
        app: web-app
    spec:
      containers:
      - name: web-app
        image: my-web-app:latest
        ports:
        - containerPort: 8080
        
        # Liveness probe - restart container if unhealthy
        livenessProbe:
          httpGet:
            path: /health
            port: 8080
          initialDelaySeconds: 30
          periodSeconds: 10
          timeoutSeconds: 5
          failureThreshold: 3
        
        # Readiness probe - remove from service if not ready
        readinessProbe:
          httpGet:
            path: /ready
            port: 8080
          initialDelaySeconds: 5
          periodSeconds: 5
          timeoutSeconds: 3
          failureThreshold: 3
        
        # Startup probe - allow slow starting containers
        startupProbe:
          httpGet:
            path: /startup
            port: 8080
          initialDelaySeconds: 10
          periodSeconds: 10
          timeoutSeconds: 5
          failureThreshold: 30

# Monitoring commands
kubectl top nodes                        # Node resource usage
kubectl top pods                         # Pod resource usage
kubectl logs -f deployment/web-app       # Follow deployment logs
kubectl logs -f -l app=web-app           # Follow logs by label
kubectl describe pod <pod-name>          # Pod events and status
kubectl get events --sort-by=.metadata.creationTimestamp  # Cluster events`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Kubernetes Security Best Practices</h2>
          
          <div style={styles.codeBlock}>
{`# Role-Based Access Control (RBAC)
apiVersion: rbac.authorization.k8s.io/v1
kind: Role
metadata:
  namespace: default
  name: pod-reader
rules:
- apiGroups: [""]
  resources: ["pods"]
  verbs: ["get", "watch", "list"]

---
apiVersion: rbac.authorization.k8s.io/v1
kind: RoleBinding
metadata:
  name: read-pods
  namespace: default
subjects:
- kind: User
  name: jane
  apiGroup: rbac.authorization.k8s.io
roleRef:
  kind: Role
  name: pod-reader
  apiGroup: rbac.authorization.k8s.io

---
# Service Account
apiVersion: v1
kind: ServiceAccount
metadata:
  name: app-service-account
  namespace: default

---
# ClusterRole for cluster-wide permissions
apiVersion: rbac.authorization.k8s.io/v1
kind: ClusterRole
metadata:
  name: node-reader
rules:
- apiGroups: [""]
  resources: ["nodes"]
  verbs: ["get", "list", "watch"]

---
# Pod Security Policy
apiVersion: policy/v1beta1
kind: PodSecurityPolicy
metadata:
  name: restricted
spec:
  privileged: false
  allowPrivilegeEscalation: false
  requiredDropCapabilities:
  - ALL
  volumes:
  - 'configMap'
  - 'emptyDir'
  - 'projected'
  - 'secret'
  - 'downwardAPI'
  - 'persistentVolumeClaim'
  runAsUser:
    rule: 'MustRunAsNonRoot'
  seLinux:
    rule: 'RunAsAny'
  fsGroup:
    rule: 'RunAsAny'

---
# Network Policy
apiVersion: networking.k8s.io/v1
kind: NetworkPolicy
metadata:
  name: web-app-netpol
spec:
  podSelector:
    matchLabels:
      app: web-app
  policyTypes:
  - Ingress
  - Egress
  ingress:
  - from:
    - podSelector:
        matchLabels:
          app: frontend
    - namespaceSelector:
        matchLabels:
          name: production
    ports:
    - protocol: TCP
      port: 8080
  egress:
  - to:
    - podSelector:
        matchLabels:
          app: database
    ports:
    - protocol: TCP
      port: 5432
  - to: []  # Allow DNS
    ports:
    - protocol: UDP
      port: 53

---
# Secure Pod Configuration
apiVersion: v1
kind: Pod
metadata:
  name: secure-pod
spec:
  serviceAccountName: app-service-account
  securityContext:
    runAsNonRoot: true
    runAsUser: 1000
    runAsGroup: 3000
    fsGroup: 2000
    seccompProfile:
      type: RuntimeDefault
  containers:
  - name: app
    image: my-secure-app:latest
    securityContext:
      allowPrivilegeEscalation: false
      readOnlyRootFilesystem: true
      capabilities:
        drop:
        - ALL
        add:
        - NET_BIND_SERVICE
    resources:
      limits:
        memory: "512Mi"
        cpu: "500m"
      requests:
        memory: "256Mi"
        cpu: "250m"
    volumeMounts:
    - name: tmp
      mountPath: /tmp
    - name: var-run
      mountPath: /var/run
  volumes:
  - name: tmp
    emptyDir: {}
  - name: var-run
    emptyDir: {}

# Security scanning and compliance
kubectl auth can-i create pods --as=jane                    # Check permissions
kubectl get rolebindings,clusterrolebindings --all-namespaces  # List all bindings
kubectl describe psp restricted                             # Pod security policy details`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Kubernetes Operations</h2>
          
          <div style={styles.codeBlock}>
{`# Cluster Management Commands

# Cluster information
kubectl cluster-info
kubectl get nodes
kubectl describe node <node-name>
kubectl get componentstatuses

# Namespace management
kubectl create namespace production
kubectl get namespaces
kubectl config set-context --current --namespace=production
kubectl config view --minify | grep namespace

# Resource management
kubectl get all                              # All resources in current namespace
kubectl get all --all-namespaces            # All resources in all namespaces
kubectl api-resources                        # List all resource types
kubectl explain pod                          # Resource documentation
kubectl explain pod.spec.containers         # Nested field documentation

# Troubleshooting
kubectl describe pod <pod-name>              # Pod details and events
kubectl logs <pod-name>                      # Pod logs
kubectl logs <pod-name> -c <container-name>  # Container logs
kubectl logs -f <pod-name>                   # Follow logs
kubectl exec -it <pod-name> -- /bin/bash     # Execute shell in pod
kubectl port-forward <pod-name> 8080:80      # Port forwarding
kubectl cp <pod-name>:/path/to/file ./file   # Copy files from pod

# Resource scaling
kubectl scale deployment web-app --replicas=5
kubectl autoscale deployment web-app --min=2 --max=10 --cpu-percent=80

# Rolling updates
kubectl set image deployment/web-app container=image:tag
kubectl rollout status deployment/web-app
kubectl rollout history deployment/web-app
kubectl rollout undo deployment/web-app

# Resource cleanup
kubectl delete pod <pod-name>
kubectl delete deployment <deployment-name>
kubectl delete service <service-name>
kubectl delete -f deployment.yaml
kubectl delete all -l app=web-app           # Delete by label

# Backup and restore
kubectl get all -o yaml > backup.yaml       # Backup resources
kubectl apply -f backup.yaml                # Restore resources

# Performance and debugging
kubectl top nodes                           # Node resource usage
kubectl top pods                            # Pod resource usage
kubectl get events --sort-by=.metadata.creationTimestamp
kubectl describe node <node-name> | grep -A5 "Allocated resources"

# Advanced operations
kubectl cordon <node-name>                  # Mark node as unschedulable
kubectl drain <node-name>                   # Evict pods from node
kubectl uncordon <node-name>                # Mark node as schedulable
kubectl taint nodes <node-name> key=value:NoSchedule  # Add node taint
kubectl label nodes <node-name> disktype=ssd          # Add node label

# Configuration management
kubectl config get-contexts                 # List contexts
kubectl config use-context <context-name>   # Switch context
kubectl config set-credentials <user> --token=<token>  # Set user credentials

# Helm package manager
helm repo add stable https://charts.helm.sh/stable
helm repo update
helm search repo nginx
helm install my-nginx stable/nginx-ingress
helm list
helm upgrade my-nginx stable/nginx-ingress
helm uninstall my-nginx`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Production Best Practices</h2>
          
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Resource Limits:</strong> Always set CPU and memory limits for containers</li>
            <li style={styles.listItem}><strong>Health Checks:</strong> Implement liveness and readiness probes</li>
            <li style={styles.listItem}><strong>Security:</strong> Use RBAC, network policies, and pod security standards</li>
            <li style={styles.listItem}><strong>Monitoring:</strong> Deploy comprehensive monitoring and logging solutions</li>
            <li style={styles.listItem}><strong>High Availability:</strong> Run multiple replicas and use anti-affinity rules</li>
            <li style={styles.listItem}><strong>Backup:</strong> Regular backups of etcd and persistent volumes</li>
            <li style={styles.listItem}><strong>Updates:</strong> Use rolling updates and canary deployments</li>
            <li style={styles.listItem}><strong>Secrets:</strong> Never store secrets in container images</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            Kubernetes is a powerful platform for container orchestration that enables you to deploy, 
            scale, and manage containerized applications at scale. Understanding its core concepts—pods, 
            deployments, services, and configuration management—is essential for building robust, 
            cloud-native applications.
          </p>
          
          <p style={styles.paragraph}>
            Start with simple deployments and gradually adopt advanced features like ingress, persistent 
            volumes, and monitoring as your applications grow in complexity. Focus on security, 
            observability, and operational best practices to ensure your Kubernetes clusters are 
            production-ready and maintainable.
          </p>
        </section>
      </main>
    </article>
  );
};

export default KubernetesGuide;
